import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Perfect for getting started',
    features: ['1,000 contacts', '5,000 emails/month', 'Basic templates', 'Campaign builder', 'Email support', 'Basic analytics'],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Starter',
    price: '$29',
    period: '/month',
    description: 'For growing businesses',
    features: ['10,000 contacts', '50,000 emails/month', 'AI email writing', 'Subject line generator', 'Advanced analytics', 'A/B testing', 'Priority support', 'Custom domains'],
    cta: 'Start Free Trial',
    highlighted: true,
  },
  {
    name: 'Pro',
    price: '$79',
    period: '/month',
    description: 'For serious marketers',
    features: ['50,000 contacts', 'Unlimited emails', 'Full AI suite', 'AI workflow builder', 'Campaign optimization', 'Send-time optimization', 'Dedicated support', 'API access', 'Custom integrations'],
    cta: 'Start Free Trial',
    highlighted: false,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations',
    features: ['Unlimited contacts', 'Unlimited emails', 'Priority AI processing', 'Custom AI models', 'SLA guarantee', 'Dedicated account manager', 'On-premise option', 'Custom training'],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

const faqs = [
  { q: 'Can I switch plans at any time?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.' },
  { q: 'What happens if I exceed my email limit?', a: "You'll receive a notification when you reach 80% of your limit. If you exceed it, emails will queue until the next billing cycle or you can upgrade your plan." },
  { q: 'Is there a free trial for paid plans?', a: 'Yes, all paid plans come with a 14-day free trial. No credit card required.' },
  { q: 'Do you offer discounts for nonprofits?', a: 'Yes, we offer 50% off for verified nonprofits and educational institutions. Contact our sales team for details.' },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Start free and scale as you grow. No hidden fees, no surprises.</p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div key={plan.name} className={`p-8 rounded-2xl border-2 ${plan.highlighted ? 'border-blue-600 bg-white shadow-xl relative' : 'border-gray-200 bg-white'}`}>
                {plan.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">Most Popular</span>
                )}
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                <p className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  {plan.period && <span className="text-gray-500">{plan.period}</span>}
                </p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  className={`mt-8 block text-center px-6 py-3 rounded-xl font-medium transition-colors ${
                    plan.highlighted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-gray-900">{faq.q}</h3>
                <p className="text-sm text-gray-600 mt-2">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold">Ready to get started?</h2>
          <p className="mt-2 text-blue-100">Start your free trial today. No credit card required.</p>
          <Link to="/login" className="inline-block mt-6 px-8 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-colors">
            Start Free Trial
          </Link>
        </div>
      </section>
    </div>
  );
}
